import { Filtering, Pagination, PaginationResponse } from '@ekkogmbh/apisdk';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { StyledSelectField } from './StyledSelectField';
import {
  alpha,
  Box,
  Chip,
  IconButton,
  ListSubheader,
  MenuItem,
  SelectProps,
  Skeleton,
  Typography,
  styled,
  Stack,
} from '@mui/material';
import { ArrowBackIos, ArrowForwardIos, Refresh } from '@mui/icons-material';
import useRequest from 'src/Common/Hooks/useRequest';

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  '&.Mui-selected': {
    backgroundColor: alpha(theme.palette.secondary.main, 0.2),
    '&:hover': {
      backgroundColor: theme.palette.secondary.main,
    },
  },
}));

interface Props<T> {
  fetchPaginatedItems: (pagination: Pagination) => Promise<PaginationResponse<T>>;
  itemId: (item: T) => string;
  itemRender: (item: T) => ReactNode;
  onChange: (item?: T | T[]) => void;
  label: string;
  value?: T | T[];
  optional?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  filtering?: Filtering;
  disableUnderline?: SelectProps['disableUnderline'];
  variant?: SelectProps['variant'];
  onClose?: SelectProps['onClose'];
  onOpen?: SelectProps['onOpen'];
  reloadable?: boolean;
  /** For multi-select: alters the rendered value inside the text field */
  multipleVariant?: 'chip' | 'count';
}

export default function PaginatedSelect<T>({
  value,
  label,
  optional,
  disabled,
  multiple,
  filtering,
  fetchPaginatedItems,
  itemId,
  itemRender,
  onChange,
  variant,
  onClose,
  onOpen,
  disableUnderline,
  reloadable,
  multipleVariant,
}: Props<T> & SelectProps): React.JSX.Element {
  const [isLoading, startLoading] = useRequest<void>();
  const [page, setPage] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(1);
  const [items, setItems] = useState<T[]>([]);
  const [reservedItems, setReservedItems] = useState<T[]>([]);
  const [selectedIndices, setSelectedIndices] = useState<number[] | undefined>(undefined);
  const [pagination, setPagination] = useState<Pagination>({ limit: 9, page });

  /** find index for given item from given array of items */
  const findIndex = useCallback((needle: T, hay: T[]) => hay.findIndex((i) => itemId(i) === itemId(needle)), [itemId]);

  /** guard against an issues where passing filtering causes re-rendering without changes (likely specifically an issue with coordinate selector?) */
  useEffect(() => {
    const { limit, page, sort, direction, filter } = pagination;
    if (
      filtering?.sort !== sort ||
      filtering?.direction !== direction ||
      JSON.stringify(filtering?.filter) !== JSON.stringify(filter)
    ) {
      setPagination({ limit, page, ...filtering });
    }
  }, [filtering]);

  useEffect(() => {
    setPagination({ ...pagination, page });
  }, [page]);

  const fetchItems = useCallback(() => {
    if (isLoading) {
      return;
    }
    startLoading(async () => {
      const result = await fetchPaginatedItems(pagination);

      const newItems: T[] = result.items ?? [];

      let newReserve: T[] = [];
      if (value) {
        newReserve = (Array.isArray(value) ? value : [value]).filter((v) => findIndex(v, newItems) === -1);
      }
      setReservedItems(newReserve);
      setItems(newItems);
      setPageCount(result.pageCount ?? 1);
    });
  }, [fetchPaginatedItems, pagination, isLoading]);

  /** reactive fetch */
  useEffect(() => {
    fetchItems();
  }, [pagination]);

  // realign currently chosen values with available data
  useEffect(() => {
    if (value === undefined) {
      if (optional) {
        setSelectedIndices([-(reservedItems.length + 1)]);
      } else if (multiple) {
        setSelectedIndices([]);
      } else {
        // maybe need to use an error when none exist
        setSelectedIndices(items.length > 0 ? [0] : []);
      }
    } else {
      if (Array.isArray(value)) {
        const reserveIndices = [...reservedItems.keys()].map((i) => -(i + 1));
        const itemIndices: number[] = value.reduce((carry: number[], item) => {
          const itmIdx = findIndex(item, items);
          if (itmIdx >= 0) {
            carry.push(itmIdx);
          }
          return carry;
        }, []);
        setSelectedIndices(reserveIndices.concat(itemIndices));
      } else {
        const itmIdx = findIndex(value, items);
        if (itmIdx >= 0) {
          setSelectedIndices([itmIdx]);
        } else {
          const reserveIdx = findIndex(value, reservedItems);
          setSelectedIndices(reserveIdx >= 0 ? [-(reserveIdx + 1)] : []);
        }
      }
    }
  }, [reservedItems, items]);

  /** Get item from either the fetched or reserved items if available. Otherwise undefined */
  const getItem = useCallback(
    (index: number): T | undefined => {
      let item: T | undefined = undefined;
      if (index < 0) {
        item = reservedItems[-index - 1];
      } else {
        item = items[index];
      }
      return item;
    },
    [items, reservedItems],
  );

  /** on change callback */
  useEffect(() => {
    if (selectedIndices === undefined || isLoading || disabled) {
      return;
    }

    if (multiple) {
      onChange(selectedIndices.map((idx) => getItem(idx)!));
    } else if (selectedIndices.length === 0 && optional) {
      onChange(undefined);
    } else {
      onChange(getItem(selectedIndices[0]));
    }
  }, [selectedIndices, isLoading, disabled]);

  const entries: ReactNode[] = useMemo(() => {
    const reservedList: ReactNode[] = reservedItems.map((item: T, idx: number) => (
      <StyledMenuItem key={`reserved item ${idx}`} value={-(idx + 1)}>
        {itemRender(item)}
      </StyledMenuItem>
    ));
    const itemList: ReactNode[] = items.map((item: T, idx: number) => (
      <StyledMenuItem key={`item ${idx}`} value={idx}>
        {itemRender(item)}
      </StyledMenuItem>
    ));

    const list = reservedList.concat(itemList);

    if (optional) {
      list.unshift(
        <StyledMenuItem key={`item optional`} value={-(reservedItems.length + 1)}>
          {'-'}
        </StyledMenuItem>,
      );
    }

    return list;
  }, [items, reservedItems, optional, value, itemRender]);

  const handleChipDelete = useCallback(
    (indexToRemove: number) => {
      if (!multiple || !selectedIndices) {
        return;
      }
      const newIndices = selectedIndices.filter((idx) => idx !== indexToRemove);
      setSelectedIndices(newIndices);
    },
    [multiple, selectedIndices],
  );

  const renderChipValue = useCallback(
    (selected: number[]) => (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {selected.map((idx) => {
          const item = getItem(idx);
          if (!item) {
            return null;
          }
          return (
            <Chip
              key={itemId(item)}
              label={itemRender(item)}
              size="small"
              onDelete={() => handleChipDelete(idx)}
              onMouseDown={(e) => e.stopPropagation()}
            />
          );
        })}
      </Box>
    ),
    [getItem, itemId, itemRender, handleChipDelete],
  );

  const renderValue = useMemo(() => {
    if (multiple === undefined) {
      return undefined;
    }
    switch (multipleVariant) {
      case 'chip':
        return renderChipValue as (selected: unknown[]) => React.JSX.Element;
      case 'count': {
        const renderCount = (selected: unknown[]) => (
          <Stack direction={'row'} spacing={2}>
            <Typography>{selected.length}</Typography>
            <Typography>{'selected'}</Typography>
          </Stack>
        );
        return renderCount;
      }
      default:
        return undefined;
    }
  }, [multiple, multipleVariant, renderChipValue]);

  const paginationSubheader = useMemo(
    () => (
      <ListSubheader sx={{ borderTop: 1, borderColor: 'divider', py: 1 }}>
        <Stack direction={'row'} justifyContent={'center'} alignItems={'center'} spacing={1}>
          <IconButton size="small" disabled={page === 1} title={'prev'} onClick={() => setPage(page - 1)}>
            <ArrowBackIos sx={{ fontSize: 14 }} />
          </IconButton>
          <Typography variant="body2" color="text.secondary">
            {`${page}/${pageCount}`}
          </Typography>
          <IconButton size="small" disabled={page === pageCount} title={'next'} onClick={() => setPage(page + 1)}>
            <ArrowForwardIos sx={{ fontSize: 14 }} />
          </IconButton>
        </Stack>
      </ListSubheader>
    ),
    [page, pageCount],
  );

  if (selectedIndices === undefined) {
    return <Skeleton sx={{ width: '100%', height: 100 }} />;
  }

  return (
    <StyledSelectField
      {...(disableUnderline && { disableUnderline })}
      variant={variant}
      disabled={disabled || isLoading}
      value={multiple ? selectedIndices : (selectedIndices[0] ?? '')}
      label={label}
      multiple={multiple}
      onChange={(e) => {
        setSelectedIndices(multiple ? (e.target.value as number[]) : [e.target.value as number]);
      }}
      onClose={onClose}
      onOpen={onOpen}
      renderValue={renderValue as (value: unknown) => ReactNode}
      action={
        reloadable !== false
          ? {
              icon: Refresh,
              onClick: fetchItems,
              disabled,
              processing: isLoading,
            }
          : undefined
      }
    >
      {entries}
      {pageCount > 1 && paginationSubheader}
    </StyledSelectField>
  );
}
